import { useEffect, useRef, useCallback } from 'react'

/**
 * Integrates beat navigation with the browser's history stack.
 *
 * - Pushes a synthetic `{ bubot: true }` entry on each forward beat
 * - Intercepts `popstate` to call `onPrev` instead of leaving the game
 * - When `canGoPrev` is false (beat 0, thread 0), lets browser back work normally
 * - Uses a `directionRef` to avoid pushing when navigating backward
 *
 * @param {Object} opts
 * @param {boolean} opts.canGoPrev - Whether the player can go to a previous beat
 * @param {Function} opts.onPrev - Callback to navigate to the previous beat
 * @param {number} opts.currentIndex - Current beat index within the thread
 * @param {number} opts.currentThreadIndex - Current thread index
 * @param {boolean} opts.isActive - Whether the game is actively playing
 * @returns {{ markBackward: Function }} Call before UI back button triggers handlePrev
 */
export function useBeatHistory({ canGoPrev, onPrev, currentIndex, currentThreadIndex, isActive }) {
  const directionRef = useRef('forward')
  const initializedRef = useRef(false)
  // When the UI back button calls markBackward(), history.back() fires popstate
  // asynchronously. This flag tells the popstate handler to skip that event since
  // the UI button already called handlePrev directly.
  const skipNextPopStateRef = useRef(false)

  // Push one initial entry so the first browser-back has something to intercept
  useEffect(() => {
    if (isActive && !initializedRef.current) {
      window.history.pushState({ bubot: true }, '')
      initializedRef.current = true
    }
    if (!isActive) {
      initializedRef.current = false
    }
  }, [isActive])

  // Push a history entry on each forward beat navigation
  useEffect(() => {
    if (!isActive || !initializedRef.current) return

    if (directionRef.current === 'forward') {
      window.history.pushState({ bubot: true }, '')
    }

    // Reset direction for next navigation
    directionRef.current = 'forward'
  }, [currentIndex, currentThreadIndex, isActive])

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    if (!isActive) return

    const handlePopState = (e) => {
      // UI back button already handled navigation — just consume the event
      if (skipNextPopStateRef.current) {
        skipNextPopStateRef.current = false
        return
      }
      if (canGoPrev) {
        // Intercept: go to previous beat instead of leaving the game
        directionRef.current = 'backward'
        onPrev()
      }
      // When canGoPrev is false, let the browser navigate normally (exit game)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [isActive, canGoPrev, onPrev])

  // Called by the UI back button before triggering handlePrev
  const markBackward = useCallback(() => {
    directionRef.current = 'backward'
    skipNextPopStateRef.current = true
    // Go back in history to consume the entry we pushed on forward navigation
    window.history.back()
  }, [])

  return { markBackward }
}
